import React, { useMemo, useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useNotifications } from '../../store/useStore';
import type { Database } from '../../types/database.types';
import { createInvoice, calculateInvoiceTotals, type DocumentType } from '../../lib/invoiceUtils';
import { supabase } from '../../lib/supabase';
import { Package } from 'lucide-react';

type Customer = Database['public']['Tables']['customers']['Row'];
type Product = Database['public']['Tables']['products']['Row'];
type PaymentMethod = Database['public']['Tables']['payment_methods']['Row'];
type Invoice = Database['public']['Tables']['invoices']['Row'];

interface CreditNoteFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
}

interface InvoiceItem {
  id: string;
  product_id: string | null;
  product: Product | null;
  description: string;
  quantity: number;
  unit: string;
  unit_price: number;
  discount_percentage: number;
  vat_rate: number;
}

export const CreditNoteFormModal: React.FC<CreditNoteFormModalProps> = ({
  isOpen,
  onClose,
  onCreated,
}) => {
  const { addNotification } = useNotifications();

  const [invoiceDate, setInvoiceDate] = useState(
    () => new Date().toISOString().slice(0, 10)
  );
  const [items, setItems] = useState<InvoiceItem[]>([]);
  const [status, setStatus] = useState<'draft' | 'issued'>('issued');
  const [saving, setSaving] = useState(false);
  const [paymentTerms, setPaymentTerms] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  // Campi specifici per Nota di Credito
  const [referenceInvoices, setReferenceInvoices] = useState<Invoice[]>([]);
  const [selectedReferenceInvoiceId, setSelectedReferenceInvoiceId] = useState<string>('');
  const [creditNoteReason, setCreditNoteReason] = useState<string>('');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [loadingReferenceInvoice, setLoadingReferenceInvoice] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [paymentMethodsResult, invoicesResult] = await Promise.all([
          supabase
            .from('payment_methods')
            .select('*')
            .eq('is_active', true)
            .order('name'),
          // Carica solo fatture emesse (issued) per riferimento
          supabase
            .from('invoices')
            .select('*')
            .eq('status', 'issued')
            .in('document_type', ['invoice', 'invoice_with_delivery'])
            .order('invoice_date', { ascending: false })
            .limit(100),
        ]);

        if (paymentMethodsResult.error) throw paymentMethodsResult.error;
        if (invoicesResult.error) throw invoicesResult.error;

        setPaymentMethods(paymentMethodsResult.data || []);
        setReferenceInvoices(invoicesResult.data || []);
      } catch (error: any) {
        console.error('Errore caricamento dati:', error);
      }
    };

    if (isOpen) {
      loadData();
    }
  }, [isOpen]);

  // Carica i dati della fattura quando viene selezionata
  useEffect(() => {
    const loadReferenceInvoiceData = async () => {
      if (!selectedReferenceInvoiceId) {
        // Reset form quando non c'è selezione
        setSelectedCustomer(null);
        setItems([]);
        setInvoiceDate(new Date().toISOString().slice(0, 10));
        setPaymentTerms('');
        setNotes('');
        return;
      }

      setLoadingReferenceInvoice(true);
      try {
        // Carica la fattura con tutti i dati
        const { data: invoiceData, error: invoiceError } = await supabase
          .from('invoices')
          .select(`
            *,
            customers (*),
            invoice_items (
              *,
              products (id, code, name, category, unit, base_price, photo_url, description, iva)
            )
          `)
          .eq('id', selectedReferenceInvoiceId)
          .single();

        if (invoiceError) throw invoiceError;

        // Imposta il cliente
        if (invoiceData.customers) {
          setSelectedCustomer(invoiceData.customers);
        }

        // Imposta la data della nota di credito (data corrente)
        setInvoiceDate(new Date().toISOString().slice(0, 10));

        // Imposta payment terms dalla fattura originale
        setPaymentTerms(invoiceData.payment_terms || '');

        // Imposta le note dalla fattura originale
        setNotes(invoiceData.notes || '');

        // Converti gli invoice_items in InvoiceItem[]
        if (invoiceData.invoice_items && invoiceData.invoice_items.length > 0) {
          const convertedItems: InvoiceItem[] = invoiceData.invoice_items.map((item: any) => ({
            id: `item-${Date.now()}-${Math.random()}`,
            product_id: item.product_id,
            product: item.products,
            description: item.description || item.products?.name || '',
            quantity: item.quantity,
            unit: item.unit || item.products?.unit || 'pz',
            unit_price: item.unit_price,
            discount_percentage: item.discount_percentage || 0,
            vat_rate: item.vat_rate || item.products?.iva || 22,
          }));
          setItems(convertedItems);
        } else {
          setItems([]);
        }
      } catch (error: any) {
        console.error('Errore caricamento fattura di riferimento:', error);
        addNotification({
          type: 'error',
          title: 'Errore',
          message: 'Impossibile caricare i dati della fattura selezionata',
        });
      } finally {
        setLoadingReferenceInvoice(false);
      }
    };

    loadReferenceInvoiceData();
  }, [selectedReferenceInvoiceId, addNotification]);

  const resetForm = () => {
    setSelectedCustomer(null);
    setInvoiceDate(new Date().toISOString().slice(0, 10));
    setItems([]);
    setStatus('issued');
    setPaymentTerms('');
    setNotes('');
    // Reset campi Nota di Credito
    setSelectedReferenceInvoiceId('');
    setCreditNoteReason('');
  };

  const handleClose = () => {
    if (saving) return;
    resetForm();
    onClose();
  };

  const handleSave = async () => {
    if (!selectedReferenceInvoiceId) {
      addNotification({
        type: 'error',
        title: 'Fattura di riferimento mancante',
        message: 'Seleziona la fattura di riferimento per la nota di credito.',
      });
      return;
    }

    if (!selectedCustomer) {
      addNotification({
        type: 'error',
        title: 'Errore',
        message: 'Errore nel caricamento dei dati della fattura.',
      });
      return;
    }

    const validItems = items.filter(
      (item) => item.description.trim() && item.quantity > 0 && item.unit_price >= 0
    );

    if (validItems.length === 0) {
      addNotification({
        type: 'error',
        title: 'Prodotti mancanti',
        message: 'La fattura selezionata non contiene prodotti.',
      });
      return;
    }

    setSaving(true);
    try {
      const invoiceItems = validItems.map((item) => ({
        product_id: item.product_id,
        description: item.description,
        quantity: item.quantity,
        unit: item.unit,
        unit_price: item.unit_price,
        discount_percentage: item.discount_percentage,
        vat_rate: item.vat_rate,
      }));

      await createInvoice({
        customer_id: selectedCustomer.id,
        order_id: null,
        invoice_date: invoiceDate,
        payment_terms: paymentTerms || selectedCustomer.payment_terms || null,
        notes: notes || null,
        status,
        document_type: 'credit_note',
        items: invoiceItems,
        // Campi specifici Nota di Credito
        reference_invoice_id: selectedReferenceInvoiceId,
        credit_note_reason: creditNoteReason || null,
      });

      addNotification({
        type: 'success',
        title: 'Documento creato',
        message: 'Il documento è stato creato correttamente.',
      });

      resetForm();
      onCreated();
      onClose();
    } catch (error: any) {
      console.error('Errore salvataggio nota di credito:', error);
      
      let errorMessage = 'Si è verificato un errore durante la creazione del documento.';
      
      if (error?.message) {
        errorMessage = error.message;
      } else if (error?.details) {
        errorMessage = error.details;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }
      
      addNotification({
        type: 'error',
        title: 'Errore creazione documento',
        message: errorMessage,
      });
    } finally {
      setSaving(false);
    }
  };

  const totals = useMemo(() => {
    const invoiceItems = items.map((item) => ({
      product_id: item.product_id,
      description: item.description || '',
      quantity: item.quantity,
      unit: item.unit,
      unit_price: item.unit_price,
      discount_percentage: item.discount_percentage,
      vat_rate: item.vat_rate,
    }));
    return calculateInvoiceTotals(invoiceItems);
  }, [items]);

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
        <DialogContent className="flex flex-col gap-3 overflow-hidden sm:max-h-[90vh] sm:max-w-6xl">
          <DialogHeader>
            <DialogTitle>Nuova Nota di Credito</DialogTitle>
          </DialogHeader>

          <div className="flex-1 min-h-0 overflow-y-auto space-y-4 py-2">
            {/* Dati Nota di Credito */}
            <div className="space-y-4 border rounded-md p-4 bg-red-50 border-red-200">
              <h3 className="text-lg font-semibold text-red-800">Fattura di Riferimento *</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>Seleziona Fattura</Label>
                  <Select
                    value={selectedReferenceInvoiceId}
                    onValueChange={setSelectedReferenceInvoiceId}
                    disabled={loadingReferenceInvoice}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleziona la fattura da rettificare" />
                    </SelectTrigger>
                    <SelectContent>
                      {referenceInvoices.map((invoice) => (
                        <SelectItem key={invoice.id} value={invoice.id}>
                          {invoice.invoice_number || `Fattura ${invoice.id.substring(0, 8)}`} - {new Date(invoice.invoice_date).toLocaleDateString('it-IT')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {loadingReferenceInvoice && (
                    <p className="text-xs text-gray-500 mt-1">Caricamento dati fattura...</p>
                  )}
                </div>
                <div className="space-y-1">
                  <Label>Causale</Label>
                  <Input
                    value={creditNoteReason}
                    onChange={(e) => setCreditNoteReason(e.target.value)}
                    placeholder="Es: Errore fatturazione, Reso merce, etc."
                  />
                </div>
              </div>
            </div>

            {/* Cliente (sola lettura, preso dalla fattura) */}
            {selectedCustomer && (
              <div className="space-y-1">
                <Label>Cliente</Label>
                <Input
                  readOnly
                  value={selectedCustomer.company_name}
                  className="bg-gray-50"
                />
              </div>
            )}

            {/* Dati fattura */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Data fattura *</Label>
                <Input
                  type="date"
                  value={invoiceDate}
                  onChange={(e) => setInvoiceDate(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label>Stato *</Label>
                <select
                  className="w-full h-10 px-3 py-2 text-sm border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                  value={status}
                  onChange={(e) => setStatus(e.target.value as 'draft' | 'issued')}
                >
                  <option value="draft">Bozza</option>
                  <option value="issued">Emessa</option>
                </select>
              </div>
            </div>


            {/* Modalità di pagamento e note */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Modalità di pagamento</Label>
                <Select
                  value={paymentTerms || ''}
                  onValueChange={setPaymentTerms}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={selectedCustomer?.payment_terms || "Seleziona modalità di pagamento"} />
                  </SelectTrigger>
                  <SelectContent>
                    {selectedCustomer?.payment_terms && !paymentMethods.some(pm => pm.name === selectedCustomer.payment_terms) && (
                      <SelectItem value={selectedCustomer.payment_terms}>
                        {selectedCustomer.payment_terms} (da cliente)
                      </SelectItem>
                    )}
                    {paymentMethods.map((method) => (
                      <SelectItem key={method.id} value={method.name}>
                        {method.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!paymentTerms && selectedCustomer?.payment_terms && (
                  <p className="text-xs text-gray-500 mt-1">
                    Default cliente: {selectedCustomer.payment_terms}
                  </p>
                )}
              </div>
              <div className="space-y-1">
                <Label>Note</Label>
                <Input
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Note aggiuntive (opzionale)"
                />
              </div>
            </div>

            {/* Prodotti (sola lettura, presi dalla fattura) */}
            {items.length > 0 && (
              <div className="space-y-3 border rounded-md p-3 bg-green-50 border-green-200">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <Package className="w-5 h-5 text-green-600" />
                  <Label className="mb-0 text-lg font-semibold text-green-800">
                    Prodotti ({items.length})
                  </Label>
                </div>

                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {items.map((item) => (
                    <div key={item.id} className="bg-white border border-green-200 rounded p-2">
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3 sm:flex-wrap">
                        <div className="flex items-center gap-2 w-full min-w-0 sm:min-w-[220px] sm:flex-1">
                          {item.product?.photo_url && (
                            <img
                              src={item.product.photo_url}
                              alt={item.product.name}
                              className="w-8 h-8 object-cover rounded border"
                            />
                          )}
                          <div className="flex-1">
                            <p className="text-xs font-medium text-gray-900">{item.description}</p>
                            {item.product && (
                              <p className="text-xs text-gray-500">{item.product.code}</p>
                            )}
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-x-3 gap-y-1 sm:contents">
                        <div className="text-xs text-gray-600 sm:min-w-[100px]">
                          Qty: {item.quantity} {item.unit}
                        </div>
                        <div className="text-xs text-gray-600 sm:min-w-[100px]">
                          Prezzo: €{item.unit_price.toFixed(2)}
                        </div>
                        {item.discount_percentage > 0 && (
                          <div className="text-xs text-green-700 font-semibold sm:min-w-[70px]">
                            Sconto {item.discount_percentage}%
                          </div>
                        )}
                        <div className="text-xs text-gray-600 sm:min-w-[80px]">
                          IVA: {item.vat_rate}%
                        </div>
                        <div className="text-xs font-semibold text-gray-900 col-span-2 sm:min-w-[100px] sm:text-right">
                          Totale: €{((item.quantity * item.unit_price * (1 - item.discount_percentage / 100)) * (1 + item.vat_rate / 100)).toFixed(2)}
                        </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Totali */}
                <div className="pt-2 border-t border-green-300 text-xs text-gray-700 flex flex-wrap justify-end gap-x-4 gap-y-1">
                  <div>Imponibile: € {totals.taxable_amount.toFixed(2)}</div>
                  <div>IVA: € {totals.tax_amount.toFixed(2)}</div>
                  <div className="font-semibold">
                    Totale: € {totals.total_amount.toFixed(2)}
                  </div>
                </div>
              </div>
            )}

            {!selectedReferenceInvoiceId && (
              <div className="text-center py-8 border rounded-md bg-gray-50">
                <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-sm text-gray-600">
                  Seleziona una fattura per vedere i prodotti
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleClose} disabled={saving}>
              Annulla
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Salvataggio...' : 'Salva nota di credito'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </>
  );
};
